import { useQuery } from '@tanstack/react-query';
import { getLongOngoingTrips } from '../../services/TripsService';
import { LongOngoingTrip, LongOngoingTripsResponse } from '../../types/Trips';

type Result = {
  trips: LongOngoingTrip[];
  /** How long a trip must have been ongoing to be listed. 0 until the first response arrives. */
  thresholdMs: number;
  isLoading: boolean;
  isError: boolean;
};

/**
 * Ongoing trips (no `endAt`) that have been running long enough to be closed manually.
 *
 * The threshold and each trip's `suggestedEndAt` are both resolved by the backend - the suggestion
 * needs the *next* trip on the same device/vehicle pair, which is not part of this list.
 *
 * This is a deliberately unpolled query: it is an admin clean-up tool over a whole-fleet scan, so
 * it runs once when the modal opens (`enabled` flipping true refetches the stale entry) and then
 * only after a trip is actually closed, via the invalidation in `useCloseOngoingTrip`.
 */
function useGetLongOngoingTrips(fleetId: string, enabled = true): Result {
  const { data, isLoading, isError } = useQuery(
    ['long-ongoing-trips', fleetId],
    async (): Promise<LongOngoingTripsResponse> => getLongOngoingTrips(fleetId),
    {
      retry: 1,
      retryDelay: 3000,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      enabled: !!fleetId && enabled,
    },
  );

  return {
    trips: data?.results || [],
    thresholdMs: data?.thresholdMs || 0,
    isLoading,
    isError,
  };
}

export default useGetLongOngoingTrips;
